/**
 * @fileoverview Data Normalization and Transformation Utilities
 *
 * Provides utilities for normalizing data from API responses to ensure
 * consistent field names and structure across the application. Handles
 * differences between API schema and UI expectations.
 *
 * Features:
 * - Data normalization for consistent field naming
 * - Search and filtering utilities
 * - Fallback handling for missing or malformed data
 * - Cross-compatible data transformation
 *
 * Usage:
 *   const normalizedGroups = groups.map(normalizeGroup);
 *   const filtered = filterBySearchTerm(items, 'search term');
 */

import type {
  NormalizedGroup,
  NormalizedTile,
  NormalizedWorkspace,
  RawGroup,
  RawTile,
  RawWorkspace,
} from "../types/entities";

export type {
  NormalizedGroup,
  NormalizedTile,
  NormalizedWorkspace,
  RawGroup,
  RawTile,
  RawWorkspace,
} from "../types/entities";

/** The fields filterBySearchTerm looks in by default. */
export interface SearchableItem {
  name?: string | null;
  title?: string | null;
  url?: string | null;
  description?: string | null;
}

/**
 * Normalize group data to have consistent field names
 */
export function normalizeGroup(
  group: RawGroup,
  fallbackIndex: number = 0,
): NormalizedGroup {
  return {
    ...group,
    id: group.group_id ?? group.id ?? fallbackIndex,
    name: group.group_name ?? group.name ?? `Group ${fallbackIndex}`,
    group_id: group.group_id ?? group.id ?? fallbackIndex,
    group_name: group.group_name ?? group.name ?? `Group ${fallbackIndex}`,
  };
}

/**
 * Normalize workspace data to have consistent field names
 */
export function normalizeWorkspace(
  workspace: RawWorkspace,
  fallbackIndex: number = 0,
): NormalizedWorkspace {
  return {
    ...workspace,
    id: workspace.workspace_id ?? workspace.id ?? fallbackIndex + 1,
    name:
      workspace.workspace_name ??
      workspace.name ??
      `Workspace ${fallbackIndex + 1}`,
    workspace_id: workspace.workspace_id ?? workspace.id ?? fallbackIndex + 1,
    workspace_name:
      workspace.workspace_name ??
      workspace.name ??
      `Workspace ${fallbackIndex + 1}`,
  };
}

/**
 * Normalize link/tile data to have consistent field names
 */
export function normalizeTile(tile: RawTile): NormalizedTile {
  return {
    ...tile,
    id: tile.tile_id ?? tile.id,
    name: tile.tile_name ?? tile.name ?? tile.title,
    title: tile.title ?? tile.tile_name ?? tile.name,
    url: tile.tile_url ?? tile.url,
    description: tile.description ?? tile.tile_description ?? "",
  };
}

/**
 * Filter items based on search term across multiple fields
 */
export function filterBySearchTerm<T extends SearchableItem>(
  items: T[],
  searchTerm: string,
  fields: (keyof T)[] = ["name", "title", "url", "description"],
): T[] {
  if (!searchTerm.trim()) return items;

  const term = searchTerm.toLowerCase();
  return items.filter((item) =>
    fields.some((field) => {
      const value = item[field];
      return typeof value === "string" && value.toLowerCase().includes(term);
    }),
  );
}

/**
 * Normalize email by stripping federated identity provider prefixes
 * (e.g., "DigicertOkta_jeremy.kast@example.com" -> "jeremy.kast@example.com")
 */
export function normalizeEmail(email: string | null | undefined): string {
  if (!email) return "";
  // Strip any prefix before underscore if it's followed by an email pattern
  const match = email.match(/^[^_]+_(.+@.+\..+)$/);
  return match ? match[1] : email;
}

/**
 * Build a stable, URL-safe anchor slug from arbitrary text.
 * Lowercases, collapses runs of non-alphanumerics to a single hyphen,
 * and trims leading/trailing hyphens.
 */
export function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}
